import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Loader2, TrendingUp, Target } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { useOpportunities, type Opportunity } from '@/hooks/useMarketIntelligence';
import { useTier } from '@/hooks/useTier';
import { TierLockedOverlay } from '@/components/TierLockedOverlay';
import { TierUpgradeModal } from '@/components/TierUpgradeModal';
import { formatReais, formatNumber } from '@/lib/utils';

const PRIORITY_SECTIONS = [
  {
    key: 'red' as const,
    emoji: '\uD83D\uDD34',
    title: 'Oportunidade Total',
    subtitle: 'Categorias sem nenhum parceiro ativo — 100% disponível',
    badgeVariant: 'destructive' as const,
    badgeLabel: 'Sem concorrência',
  },
  {
    key: 'orange' as const,
    emoji: '\uD83D\uDFE0',
    title: 'Expansão de Cobertura',
    subtitle: 'Regiões ainda não cobertas por campanhas ativas',
    badgeVariant: 'warning' as const,
    badgeLabel: 'Regiões disponíveis',
  },
  {
    key: 'yellow' as const,
    emoji: '\uD83D\uDFE1',
    title: 'Tendência de Crescimento',
    subtitle: 'Demanda acelerando — crescimento acima de 30%',
    badgeVariant: 'info' as const,
    badgeLabel: 'Em alta',
  },
];

function OpportunityCard({ opp }: { opp: Opportunity }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div className="space-y-1">
          <CardTitle className="text-base font-bold text-foreground">
            {opp.category}
          </CardTitle>
          {opp.region && (
            <p className="text-xs text-muted-foreground">
              Regiões: {opp.region}
            </p>
          )}
        </div>
        <Link
          to="/campaigns/new"
          search={{
            category: opp.category,
            region: opp.region,
            suggestedBudget: opp.suggestedBudget,
          }}
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          Criar campanha agora
          <ArrowRight size={12} />
        </Link>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Demanda (90 dias)</div>
            <div className="text-lg font-bold">{formatNumber(opp.osCount)} OS</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Ticket médio</div>
            <div className="text-lg font-bold">{formatReais(opp.avgTicket)}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Orçamento sugerido</div>
            <div className="text-lg font-bold">{formatReais(opp.suggestedBudget)}/mês</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {opp.topModels.length > 0 && (
            <>
              <span className="text-xs font-medium text-muted-foreground">Principais modelos:</span>
              {opp.topModels.map((model) => (
                <Badge key={model} variant="outline">
                  {model}
                </Badge>
              ))}
            </>
          )}
          {opp.priority === 'yellow' && opp.growthPercent > 0 && (
            <Badge variant="info" className="ml-auto">
              <TrendingUp size={12} className="mr-1" />
              +{opp.growthPercent}%
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function OpportunitiesPage() {
  const { tierInfo, canAccess, tierUnknown, tierLoading, refetchTier } = useTier();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const hasAccess = canAccess('opportunities');
  const { data, isLoading, isError, error, refetch } = useOpportunities();
  const opportunities = data?.opportunities ?? [];

  // Cadeado so quando SABEMOS que o plano nao inclui. Falhou a busca do
  // tier, a tela diz isso — nao oferece upgrade para o tier ja pago.
  if (tierUnknown) {
    return (
      <ErrorState
        title="Não foi possível verificar seu plano"
        detail="Sem isso não dá para saber se as oportunidades estão incluídas no seu plano."
        onRetry={() => void refetchTier()}
      />
    );
  }

  // Carregando nao e bloqueado: sem isto a tela mostrava o cadeado e o
  // convite de upgrade no intervalo entre abrir a pagina e o dashboard
  // responder — o mesmo que o sidebar deixou de fazer com `tierKnown`.
  if (tierLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Oportunidades</h1>
          <p className="text-sm text-muted-foreground">
            Categorias com demanda real descoberta — invista onde há necessidade
          </p>
        </div>
        <TierLockedOverlay
          requiredTier="gold"
          featureLabel="Motor de Oportunidades"
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
        {showUpgradeModal && tierInfo && (
          <TierUpgradeModal currentTier={tierInfo.current} onClose={() => setShowUpgradeModal(false)} />
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // `data?.opportunities ?? []` fazia a busca falhada virar "nenhuma
  // oportunidade" — que nesta tela significa "o mercado esta todo coberto",
  // o oposto do que ela existe para dizer.
  if (isError && opportunities.length === 0) {
    return (
      <ErrorState
        title="Não foi possível carregar as oportunidades"
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  // Group by priority
  const grouped = {
    red: opportunities.filter((o) => o.priority === 'red'),
    orange: opportunities.filter((o) => o.priority === 'orange'),
    yellow: opportunities.filter((o) => o.priority === 'yellow'),
  };

  const hasAny = opportunities.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Oportunidades</h1>
        <p className="text-sm text-muted-foreground">
          Categorias com demanda real descoberta — invista onde há necessidade
        </p>
      </div>

      {!hasAny ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={<Target size={30} />}
              title="Nenhuma oportunidade no momento"
              description="As categorias da sua região já estão cobertas por campanhas ativas. Assim que surgir demanda sem cobertura, ela aparece aqui."
              action={{ label: 'Ver inteligência de mercado', href: '/market-intelligence' }}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {PRIORITY_SECTIONS.map((section) => {
            const items = grouped[section.key];
            if (items.length === 0) return null;

            return (
              <div key={section.key} className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{section.emoji}</span>
                  <h2 className="text-lg font-bold">{section.title}</h2>
                  <Badge variant={section.badgeVariant}>{section.badgeLabel}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{section.subtitle}</p>

                <div className="space-y-3">
                  {items.map((opp) => (
                    <OpportunityCard
                      key={`${opp.category}-${opp.region ?? 'all'}-${opp.priority}`}
                      opp={opp}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
